import { CONTRACT, useApproveErc1155, useContract } from '@apeiron/contract'
import {
  Account,
  useAccountContext,
  useChainSwitcher,
  useMessageContext
} from '@apeiron/temp'
import { useMktErrorHandling } from '@apeiron/wallet'
import useVerifyAccount from '@src/hooks/graphql/useVerifyAccount'
import * as R from 'ramda'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Address } from 'viem'

const useBurnPlaytime = (): Hook => {
  const { t } = useTranslation()

  const { send: doBurn } = useContract(CONTRACT.BATTLE_DEMO.BURN_TIME)

  const { verify: verifyBurnResult } = useVerifyAccount()

  const { account } = useAccountContext()

  const { getContractAddress } = useChainSwitcher()

  const approveFragment = useApproveErc1155(
    account.walletAddress as Address,
    getContractAddress(CONTRACT.BATTLE_DEMO.GET_ALLOWANCE.address)
  )

  const { setInfo } = useMessageContext()

  const { showErrorPopup } = useMktErrorHandling()

  const burnPlaytime = useCallback(
    async (burnHour: number): Promise<boolean> => {
      try {
        const burnAddress = getContractAddress(
          CONTRACT.BATTLE_DEMO.BURN_TIME.address
        )

        setInfo({
          message: t('common.wait_token_approve')
        })

        await approveFragment(burnAddress)

        setInfo({
          message: t('common.popup.transaction.be_patient')
        })

        const success = await doBurn(burnHour)

        if (success) {
          const beforeBurn = R.pathOr(
            0,
            ['game', 'battlePass', 'booty', 'remainTime'],
            account
          )

          await verifyBurnResult((verified: Account) => {
            const afterBurn = R.pathOr(
              0,
              ['game', 'battlePass', 'booty', 'remainTime'],
              verified
            )

            return beforeBurn !== afterBurn
          })
        }

        setInfo(null)

        return true
      } catch (error) {
        showErrorPopup(error)

        return false
      }
    },
    [
      account,
      approveFragment,
      doBurn,
      getContractAddress,
      setInfo,
      showErrorPopup,
      t,
      verifyBurnResult
    ]
  )

  return { burnPlaytime }
}

type Hook = {
  burnPlaytime: (burnHour: number) => Promise<boolean>
}

export default useBurnPlaytime
